import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from './apiClient';
import {
  mapToClientSummaries,
  mapToDeviceIssues,
  mapToDeviceRecords,
  mapToExecutiveSummary,
  type ApiAuditResult,
  type ApiCriticalIssue,
} from './mappers';
import type { AlertHistoryEntry, AlertSettings, PaginationMeta } from '../data/types';

/**
 * API response envelope — matches the backend ApiResponse<T> shape from src/types/api-types.ts.
 * All list endpoints return data wrapped in this structure with pagination metadata.
 */
interface ApiResponse<T> {
  data: T[];
  pagination: PaginationMeta;
  metadata: { timestamp: string; degradedPlatforms?: string[] };
}

/**
 * Builds the audit API URL with optional includeRetired query param.
 */
function buildAuditUrl(base: string, includeRetired?: boolean): string {
  if (includeRetired) {
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}includeRetired=true`;
  }
  return base;
}

/**
 * Fetches the latest audit results for all clients (up to 5000 devices).
 *
 * Returns data mapped to ClientCoverageSummary[] via mapToClientSummaries.
 * Re-fetches every 5 minutes to pick up new audit runs without page refresh.
 *
 * When `includeRetired` is true, retired devices are included in the API response.
 * The query key includes the filter so TanStack Query caches separately.
 */
export function useClientsQuery(opts?: { includeRetired?: boolean }) {
  const includeRetired = opts?.includeRetired ?? false;
  return useQuery({
    queryKey: ['audit', 'clients', { includeRetired }],
    queryFn: () =>
      apiFetch<ApiResponse<ApiAuditResult>>(
        buildAuditUrl('/api/audit/latest?pageSize=5000', includeRetired)
      ),
    select: (res) => mapToClientSummaries(res.data),
    staleTime: 5 * 60 * 1000,
  });
}

/** Lightweight device entry for cross-client agent name search. */
export interface DeviceSearchEntry {
  deviceName: string;
  clientName: string;
  clientId: string;
  osType: string;
  hasIssue: boolean;
}

/**
 * Returns a flat list of all devices for agent name search.
 *
 * Shares the same query key and cache as useClientsQuery (no extra network request)
 * but applies a different `select` to extract device-level data.
 */
export function useDeviceSearchQuery() {
  return useQuery({
    queryKey: ['audit', 'clients', { includeRetired: false }],
    queryFn: () =>
      apiFetch<ApiResponse<ApiAuditResult>>(buildAuditUrl('/api/audit/latest?pageSize=5000')),
    select: (res): DeviceSearchEntry[] =>
      res.data
        .filter((e) => !e.isRetired)
        .map((e) => ({
          deviceName: e.deviceName,
          clientName: e.clientName,
          clientId: e.partitionKey,
          osType: e.osType,
          hasIssue: e.hasCriticalIssue,
        })),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetches the latest audit results and aggregates them into a single ExecutiveSummary.
 *
 * When `includeRetired` is true, retired devices are included in the API response.
 * The query key includes the filter so TanStack Query caches separately.
 */
export function useSummaryQuery(opts?: { includeRetired?: boolean }) {
  const includeRetired = opts?.includeRetired ?? false;
  return useQuery({
    queryKey: ['audit', 'summary', { includeRetired }],
    queryFn: () =>
      apiFetch<ApiResponse<ApiAuditResult>>(
        buildAuditUrl('/api/audit/latest?pageSize=5000', includeRetired)
      ),
    select: (res) => ({
      summary: mapToExecutiveSummary(res.data),
      degradedPlatforms: res.metadata.degradedPlatforms ?? [],
    }),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetches device records for a specific client.
 *
 * Only runs when clientId is non-empty. Returns DeviceRecord[] mapped from
 * AuditResultEntity rows filtered to the given client partition.
 */
export function useClientDevicesQuery(clientId: string, opts?: { includeRetired?: boolean }) {
  const includeRetired = opts?.includeRetired ?? false;
  return useQuery({
    queryKey: ['audit', 'devices', clientId, { includeRetired }],
    queryFn: () =>
      apiFetch<ApiResponse<ApiAuditResult>>(
        buildAuditUrl(
          `/api/audit/latest?client=${encodeURIComponent(clientId)}&pageSize=5000`,
          includeRetired
        )
      ),
    select: (res) => {
      const isAddigyClient = res.data.some((e) => e.addigyStatus === 'present');
      return mapToDeviceRecords(res.data, { isAddigyClient });
    },
    enabled: Boolean(clientId),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetches critical issues, optionally filtered to a single client.
 *
 * When clientId is provided, adds a `client` query parameter to scope results.
 * Returns DeviceIssue[] mapped from CriticalIssueEntity rows.
 */
export function useIssuesQuery(clientId?: string) {
  const params = new URLSearchParams({ pageSize: '200' });
  if (clientId) {
    params.set('client', clientId);
  }

  return useQuery({
    queryKey: ['audit', 'issues', clientId ?? 'all'],
    queryFn: () =>
      apiFetch<ApiResponse<ApiCriticalIssue>>(`/api/audit/issues?${params.toString()}`),
    select: (res) => mapToDeviceIssues(res.data),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetches the ClientCoverageSummary for a single client.
 *
 * Reuses the per-client device endpoint and applies mapToClientSummaries,
 * returning the first (and only) summary entry, or null if no data found.
 * Only runs when clientId is non-empty.
 */
export function useClientSummaryQuery(clientId: string, opts?: { includeRetired?: boolean }) {
  const includeRetired = opts?.includeRetired ?? false;
  return useQuery({
    queryKey: ['audit', 'client-summary', clientId, { includeRetired }],
    queryFn: () =>
      apiFetch<ApiResponse<ApiAuditResult>>(
        buildAuditUrl(
          `/api/audit/latest?client=${encodeURIComponent(clientId)}&pageSize=5000`,
          includeRetired
        )
      ),
    select: (res) => {
      const summaries = mapToClientSummaries(res.data);
      return summaries[0] ?? null;
    },
    enabled: Boolean(clientId),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetches email alert settings (merged Table Storage + env var defaults).
 * Refreshes every 30 seconds — settings change infrequently but should update fast after save.
 */
export function useAlertSettingsQuery() {
  return useQuery({
    queryKey: ['settings', 'alerts'],
    queryFn: () => apiFetch<AlertSettings>('/api/settings/alerts'),
    staleTime: 30 * 1000,
  });
}

/**
 * Fetches alert history entries for the specified time window.
 * Returns the data array extracted from the API envelope.
 */
export function useAlertHistoryQuery(hours: number) {
  return useQuery({
    queryKey: ['alerts', 'history', hours],
    queryFn: () =>
      apiFetch<ApiResponse<AlertHistoryEntry>>(`/api/alerts/history?hours=${hours}&pageSize=200`),
    select: (res) => res.data,
    staleTime: 60 * 1000,
  });
}

/**
 * Mutation to save alert settings via PUT.
 * Invalidates the settings query cache on success so the UI reflects saved values.
 */
export function useSaveAlertSettingsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: { recipients: string[]; cooldownHours: number; enabled: boolean }) =>
      apiFetch('/api/settings/alerts', { method: 'PUT', body: settings, mutation: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings', 'alerts'] }),
  });
}

/**
 * Mutation to send a test alert email via POST.
 * No cache invalidation needed — test alerts are fire-and-forget.
 */
export function useSendTestAlertMutation() {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ sent: boolean; reason?: string; clientCount?: number; issueCount?: number }>(
        '/api/settings/alerts/test',
        { method: 'POST', mutation: true }
      ),
  });
}
